import React, { useState } from 'react';
import SidebarIcon from './SidebarIcon';

const Sidebar = ({ navTabs, activeTab, setActiveTab, user }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const handleTabClick = (tab) => {
    if (!tab.comingSoon) {
      setActiveTab(tab.id);
      setIsMobileOpen(false); // Close mobile menu after selection
    }
  };

  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-20 left-4 z-50 p-2 bg-orange-600 rounded-lg shadow-lg"
      >
        <span className="text-white text-xl">☰</span>
      </button>

      {/* Sidebar */}
      <div
        className={`
          fixed top-0 left-0 h-full border-r z-40
          transition-all duration-300 ease-in-out
          ${isCollapsed ? 'w-16' : 'w-64'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{ 
          backgroundColor: 'var(--sidebar-bg)', 
          borderColor: 'var(--sidebar-border)' 
        }}
      >
        {/* Logo Section */}
        <div className="h-20 flex items-center justify-between px-4 border-b backdrop-blur-sm" style={{ borderColor: 'var(--sidebar-border)' }}>
          {!isCollapsed && (
            <div className="flex items-center space-x-3 group">
              <div className="w-10 h-10 accent-gradient rounded-xl flex items-center justify-center shadow-lg transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                <span className="text-2xl">⚡</span>
              </div>
              <span className="text-xl font-bold accent-text">
                MaxeDigital AI
              </span>
            </div>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden lg:block p-2 rounded-lg transition-all duration-300 group"
            style={{ backgroundColor: 'transparent' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--hover-bg)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <span className="accent-text text-xl transition-colors duration-300">
              {isCollapsed ? '→' : '←'}
            </span>
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="py-4 overflow-y-auto" style={{ height: 'calc(100% - 5rem - 5rem)' }}>
          <div className="space-y-1 px-2">
            {navTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab)}
                disabled={tab.comingSoon}
                className={`
                  w-full flex items-center gap-3 px-3 py-3 rounded-xl
                  transition-all duration-300 group relative overflow-hidden
                  ${tab.comingSoon ? 'cursor-not-allowed opacity-60' : ''}
                  ${activeTab === tab.id && !tab.comingSoon
                    ? 'accent-gradient text-white shadow-lg scale-105'
                    : 'hover:scale-105'
                  }
                `}
                style={activeTab === tab.id && !tab.comingSoon ? {} : {
                  color: 'var(--text-secondary)',
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id && !tab.comingSoon) {
                    e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                    e.currentTarget.style.color = 'var(--text-primary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id && !tab.comingSoon) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--text-secondary)';
                  }
                }}
                title={tab.comingSoon ? 'Coming Soon' : tab.label}
              >
                {/* Icon with gradient background */}
                <span className={`
                  relative z-10 flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0
                  transition-all duration-300
                  ${activeTab === tab.id 
                    ? 'bg-white/20 backdrop-blur-sm shadow-inner' 
                    : ''
                  }
                `}
                style={activeTab === tab.id ? {} : { backgroundColor: 'var(--card-bg)' }}>
                  <SidebarIcon type={tab.id} className="w-5 h-5" active={activeTab === tab.id} />
                </span>
                
                {!isCollapsed && (
                  <>
                    <span className="relative z-10 flex-1 text-left text-sm font-semibold truncate transition-all duration-300">
                      {tab.label}
                    </span>
                    {tab.comingSoon && (
                      <span className="relative z-10 text-xs px-2 py-1 bg-yellow-500/10 text-yellow-400 rounded-full border border-yellow-500/30 backdrop-blur-sm">
                        Soon
                      </span>
                    )}
                    {/* Active indicator dot */}
                    {activeTab === tab.id && !tab.comingSoon && (
                      <span className="relative z-10 w-2 h-2 rounded-full bg-white shadow-lg shadow-white/50 animate-pulse"></span>
                    )}
                  </>
                )}
              </button>
            ))}
          </div>
        </nav>

        {/* User Info at Bottom - Fixed with solid background */}
        {!isCollapsed && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t" style={{ 
            borderColor: 'var(--sidebar-border)',
            backgroundColor: 'var(--sidebar-bg)',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
            zIndex: 10
          }}>
            <div className="flex items-center gap-3 group cursor-pointer p-2 rounded-xl transition-all duration-300"
              style={{ backgroundColor: 'transparent' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--hover-bg)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
              <div className="relative w-10 h-10 accent-gradient rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                <span className="relative z-10">{user.name.charAt(0).toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate transition-colors duration-300" style={{ color: 'var(--text-primary)' }}>{user.name}</p>
                <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{user.industry}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          onClick={() => setIsMobileOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
        />
      )}
    </>
  );
};

export default Sidebar;
